import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  Switch,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faEdit } from "@fortawesome/free-solid-svg-icons";
import DataTable from "react-data-table-component";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as IconButton from "../../themes/IconButtonSet";
import { tableCustomStyles, StatusActive } from "../../themes/TableStyles";

const RolesManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [officeTypes, setOfficeTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    RoleID: "",
    RoleName: "",
    RoleGroup: "",
    RoleDepartment: "",
    Remark: "",
    Status: "1",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "รหัสกลุ่มผู้ใช้งานระบบ",
      selector: (row) => row.RoleID,
      sortable: true,
    },
    {
      name: "ชื่อกลุ่มผู้ใช้งานระบบ",
      selector: (row) => row.RoleName,
      sortable: true,
    },
    {
      name: "ประเภทผู้ใช้งาน",
      selector: (row) => row.RoleGroup || "-",
      sortable: true,
    },
    {
      name: "หน่วยงาน",
      selector: (row) => {
        const dept = officeTypes.find((t) => t.OfficeTypeID === row.RoleDepartment);
        return dept ? dept.OfficeTypeName : row.RoleDepartment || "-";
      },
      sortable: true,
    },
    {
      name: "สถานะ",
      selector: (row) => <StatusActive status={row.Status} />,
      sortable: true,
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.Edit
          icon={faEdit}
          onClick={() => handleEdit(row)}
          label="แก้ไข"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchOfficeTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setOfficeTypes(response.OfficeTypeList || []);
      }
    } catch (error) {
      console.error("Error fetching office types:", error);
    }
  };

  const fetchRoles = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getRolesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.RolesList || [];
        setRoles(data);
        setFilteredRoles(data);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchRoles();
      fetchOfficeTypes();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    applyFilters(value, filterDepartment, filterStatus);
  };

  const handleDepartmentFilter = (e) => {
    const value = e.target.value;
    setFilterDepartment(value);
    applyFilters(searchTerm, value, filterStatus);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    applyFilters(searchTerm, filterDepartment, value);
  };

  const applyFilters = (search, department, status) => {
    let filtered = roles;

    // กรองด้วยคำค้นหา
    if (search) {
      const searchTerms = search.split(" ").filter((term) => term.length > 0);
      filtered = filtered.filter((row) => {
        const deptName = officeTypes.find((t) => t.OfficeTypeID === row.RoleDepartment)?.OfficeTypeName || row.RoleDepartment || "";
        return searchTerms.every((term) =>
          [
            row.RoleID.toLowerCase(),
            row.RoleName.toLowerCase(),
            row.RoleGroup.toLowerCase(),
            deptName.toLowerCase(),
            row.Status ? "active" : "inactive",
          ].some((field) => field.includes(term))
        );
      });
    }

    // กรองด้วยหน่วยงาน (ใช้ OfficeTypeID)
    if (department) {
      filtered = filtered.filter((row) => row.RoleDepartment === department);
    }

    // กรองด้วยสถานะ
    if (status) {
      filtered = filtered.filter((row) =>
        status === "1" ? row.Status : !row.Status
      );
    }

    setFilteredRoles(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilterDepartment("");
    setFilterStatus("");
    setFilteredRoles(roles);
  };

  const handleAddNew = () => {
    setFormData({
      RoleID: "",
      RoleName: "",
      RoleGroup: "",
      RoleDepartment: "",
      Remark: "",
      Status: "1",
    });
    setIsEditing(false);
    setShowForm(true);
    setErrors({});
  };

  const handleEdit = (row) => {
    setFormData({
      ...row,
      Status: row.Status ? "1" : "0",
    });
    setIsEditing(true);
    setShowForm(true);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleStatusChange = (e) => {
    setFormData({ ...formData, Status: e.target.checked ? "1" : "0" });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "RoleID":
      case "RoleName":
      case "RoleGroup":
      case "RoleDepartment":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.RoleID &&
      formData.RoleName &&
      formData.RoleGroup &&
      formData.RoleDepartment &&
      Object.keys(errors).length === 0
    );
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const apiUrl = isEditing
        ? "/updateRolesByID"
        : "/createRoles";
      const response = await apiService({
        method: "POST",
        url: apiUrl,
        data: {
          ...formData,
          Status: formData.Status === "1",
          CreateBy: sessionId,
          UpdatedBy: sessionId,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert(isEditing ? "อัปเดตกลุ่มผู้ใช้งานระบบ สำเร็จ!" : "สร้างกลุ่มผู้ใช้งานระบบ สำเร็จ!");
        setShowForm(false);
        fetchRoles();
      }
    } catch (error) {
      console.error("Error saving role:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      RoleID: "",
      RoleName: "",
      RoleGroup: "",
      RoleDepartment: "",
      Remark: "",
      Status: "1",
    });
    setErrors({});
  };

  const handleDelete = async () => {
    if (!window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบกลุ่มผู้ใช้งานระบบ นี้?")) return;
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateRolesByID",
        data: {
          ...formData,
          Status: false,
          UpdatedBy: sessionId,
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("ลบกลุ่มผู้ใช้งานระบบ สำเร็จ!");
        setShowForm(false);
        fetchRoles();
      }
    } catch (error) {
      console.error("Error deleting role:", error);
      alert("เกิดข้อผิดพลาดในการลบ: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              รายการข้อมูลกลุ่มผู้ใช้งานระบบ
            </Heading>
          </HStack>
        </Box>

        {/* ส่วนฟอร์ม */}
        {showForm && (
          <Box
            p={5}
            borderWidth={1}
            borderRadius="lg"
            boxShadow="lg"
            w="100%"
            bg="white"
          >
            <Heading size="md" mb={4}>
              {isEditing ? "แก้ไขข้อมูลกลุ่มผู้ใช้งานระบบ" : "เพิ่มข้อมูลใหม่"}
            </Heading>
            <Grid templateColumns="repeat(4, 1fr)" gap={6}>
              <GridItem colSpan={1}>
                <FormControl isRequired isInvalid={!!errors.RoleID}>
                  <FormLabel>รหัสกลุ่มผู้ใช้งานระบบ</FormLabel>
                  <Input
                    name="RoleID"
                    value={formData.RoleID}
                    onChange={handleInputChange}
                    isDisabled={isEditing}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.RoleID}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isRequired isInvalid={!!errors.RoleName}>
                  <FormLabel>ชื่อกลุ่มผู้ใช้งานระบบ</FormLabel>
                  <Input
                    name="RoleName"
                    value={formData.RoleName}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.RoleName}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isRequired isInvalid={!!errors.RoleGroup}>
                  <FormLabel>ประเภทผู้ใช้งาน</FormLabel>
                  <Input
                    name="RoleGroup"
                    value={formData.RoleGroup}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.RoleGroup}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={1}>
                <FormControl isRequired isInvalid={!!errors.RoleDepartment}>
                  <FormLabel>หน่วยงาน</FormLabel>
                  <Select
                    name="RoleDepartment"
                    value={formData.RoleDepartment}
                    onChange={handleInputChange}
                    placeholder="เลือกหน่วยงาน"
                    colorScheme="iBlue"
                  >
                    {officeTypes.map((type) => (
                      <option key={type.OfficeTypeID} value={type.OfficeTypeID}>
                        {type.OfficeTypeName}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.RoleDepartment}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel>หมายเหตุ</FormLabel>
                  <Textarea
                    name="Remark"
                    value={formData.Remark}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel>สถานะ</FormLabel>
                  <Switch
                    name="Status"
                    isChecked={formData.Status === "1"}
                    onChange={handleStatusChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
            </Grid>
            <HStack mt={6} justify="flex-end">
              <IconButton.Close onClick={handleCancel} label="ปิด" />
              {isEditing && (
                <IconButton.Delete onClick={handleDelete} label="ลบ" />
              )}
              <IconButton.Save onClick={handleSave} isLoading={isLoading} />
            </HStack>
          </Box>
        )}

        {/* ส่วนตัวกรอง */}
        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
          spacing={4}
        >
          <HStack spacing={4} flexWrap={{ base: "wrap", md: "nowrap" }}>
            <InputGroup w={{ base: "100%", md: "300px" }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหากลุ่มผู้ใช้งานระบบ..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
              />
            </InputGroup>
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filterDepartment}
              onChange={handleDepartmentFilter}
              placeholder="เลือกหน่วยงาน"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              {officeTypes.map((type) => (
                <option key={type.OfficeTypeID} value={type.OfficeTypeID}>
                  {type.OfficeTypeName}
                </option>
              ))}
            </Select>
            <Select
              w={{ base: "100%", md: "200px" }}
              value={filterStatus}
              onChange={handleStatusFilter}
              placeholder="เลือกสถานะ"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Select>
            <IconButton.Clear onClick={handleClearSearch} label="ล้าง" />
          </HStack>
          <IconButton.Add onClick={handleAddNew} label="เพิ่มข้อมูลใหม่" />
        </HStack>

        {/* ส่วนลิสต์ */}
        {isLoading ? (
          <Spinner size="md" />
        ) : roles.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลกลุ่มผู้ใช้งานระบบ
          </Text>
        ) : filteredRoles.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredRoles}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default RolesManagement;